import React from "react";

/** Ported verbatim from the legacy widget's /legal/terms page. This is legal
    copy — reword nothing without sign-off. */
function TermsContent() {
  return (
    <>
      <h1>Apointa Terms of Service</h1>

      <h2>Terms of Service – Summary</h2>
      <p>
        Hi! We are Apointa (FZC), a platform designed to help sports facilities (like academies, courts, and trainers) manage their operations. We've
        built these Terms of Service to be as transparent and straightforward as possible. This summary outlines key points, but please make sure to
        read the full version below.
      </p>
      <p>
        Apointa provides the Apointa platform (website and, in the future, possibly an app), allowing you to discover, book, and pay for services
        offered by sports facilities, academies, and coaches ("Partners"). You may also purchase related products or redeem Partner-issued promo codes
        or vouchers.
      </p>
      <p>
        The services you book or buy are offered by our Partners—not by us. Apointa simply facilitates the transaction. When you book or buy, we act
        as the Partner's commercial agent. If you pay online, Apointa collects payment on behalf of the Partner, settling your obligation with them.
      </p>
      <p>
        The contract for services and products is between you and the relevant Partner. If you're unhappy with any Partner or service, let us know at
        info@apointa.com.
      </p>
      <p>
        You agree to Partner terms (e.g. cancellation policies, deposits, or no-show charges) when confirming a booking. Check all booking details
        carefully before confirming.
      </p>
      <p>We may suspend your Apointa account if you breach these Terms or behave inappropriately toward Partners or our team.</p>
      <p>
        To reschedule or cancel a booking, or return/exchange a product (if allowed), do so via the Apointa platform, by contacting the Partner
        directly, or by emailing info@apointa.com.
      </p>

      <h2>Terms of Service – Full Version</h2>
      <p>
        These Terms of Service ("Terms") govern your use of the Apointa platform, operated by Apointa (FZC), registered in Sharjah Research Technology
        and Innovation Park, Block C VL10-049, Sharjah, UAE ("Apointa," "we," "us").
      </p>

      <h3>Key Definitions:</h3>
      <ul>
        <li>"Platform": The Apointa website and any future app (if and when available).</li>
        <li>"Partner": A third-party service provider (e.g. sports academy, coach, or venue) offering services and/or products through Apointa.</li>
        <li>"Partner Services": Services offered by a Partner, including training sessions, court rentals, and classes.</li>
        <li>"Product": Physical or digital goods sold by Partners.</li>
        <li>"Booking": A reservation for Partner Services.</li>
        <li>"Order": A purchase of Products.</li>
        <li>"Client": You, the user of Apointa.</li>
        <li>
          "Partner Contract": The agreement between you and a Partner covering any Booking or Order, including Partner's specific terms like
          cancellations or deposits.
        </li>
      </ul>

      <h3>Our Role:</h3>
      <p>
        Apointa facilitates Bookings and Orders through our platform. We are not responsible for providing the Partner Services or Products. We
        collect payments as an agent of the Partner.
      </p>

      <h3>Platform Use:</h3>
      <p>
        You may book services as a guest or create an account. You must provide accurate and updated information. Apointa or Partners may restrict
        your use if fraud or misuse is suspected.
      </p>
      <p>
        Customers using the website who are minors or under the age of 18 shall not register as users and shall not transact on or use the website.
      </p>
      <p>
        You are responsible for maintaining the confidentiality of your account, including your login credentials. Apointa is not liable for any
        unauthorized use of your account.
      </p>
      <p>Cardholders must retain a copy of transaction records and adhere to applicable merchant policies and rules.</p>
      <p>
        Apointa will not trade with or provide any services to individuals or entities based in OFAC-sanctioned countries or to individuals on OFAC's
        sanctions lists.
      </p>

      <h3>Promo Codes and Vouchers:</h3>
      <p>
        Partners may issue promo codes or vouchers. These are governed by the Partner Contract and can be withdrawn or limited at the Partner's
        discretion.
      </p>

      <h3>Payments:</h3>
      <p>
        You agree to pay the full amount for any Booking or Order. Payments made through the platform discharge your debt to the Partner. Apointa uses
        third-party processors to complete transactions securely.
      </p>
      <p>Accepted card types include Visa, MasterCard, and American Express. All payments will be processed in AED (United Arab Emirates Dirhams).</p>
      <p>
        The displayed price and currency at the checkout page will be the same price and currency printed on the Transaction Receipt, and the amount
        charged to the card will be shown in your card currency.
      </p>

      <h3>Delivery Policy:</h3>
      <p>Customers will receive payment confirmation by email immediately after successful payment.</p>

      <h3>Refund Policy:</h3>
      <p>
        Refunds will be made onto the original mode of payment and will be processed within 10 to 45 days depending on the issuing bank of the credit
        card. Refunds are subject to the Partner's individual refund and cancellation policies for the services or products offered.
      </p>

      <h3>Cancellation Policy:</h3>
      <p>
        Cancellations are governed by the Partner's terms for the offered service. Clients must review the specific cancellation terms provided at the
        time of booking, as cancellation windows, fees, or refund eligibility may vary by Partner.
      </p>

      <h3>Rescheduling, Cancellations, and No-Shows:</h3>
      <p>
        You may reschedule or cancel subject to Partner terms disclosed at the time of Booking. Partners may charge up to 100% for late cancellations
        or no-shows. Deposits may be required and can be non-refundable.
      </p>

      <h3>Taxes:</h3>
      <p>Partners are solely responsible for applicable VAT or other taxes.</p>

      <h3>Product Orders, Delivery, and Returns:</h3>
      <p>
        If you order Products, your contract is with the Partner. Delivery timelines and return policies are managed by the Partner. Apointa is not
        responsible for delayed, faulty, or misrepresented Products. Contact the Partner directly with any issues.
      </p>

      <h3>Liability:</h3>
      <p>
        Apointa is not liable for the quality, safety, legality, or outcome of any services or products offered by Partners. To the maximum extent
        permitted by law, Apointa disclaims all liability for indirect, incidental, special, or consequential damages arising out of your use of the
        Platform or reliance on Partner services.
      </p>

      <h3>Indemnity:</h3>
      <p>
        You agree to indemnify and hold harmless Apointa, its officers, affiliates, and employees from any claims, damages, or liabilities (including
        legal fees) arising from:
      </p>
      <ul>
        <li>Your use or misuse of the platform;</li>
        <li>Your breach of these Terms;</li>
        <li>Any violation of third-party rights;</li>
        <li>Any issues between you and a Partner.</li>
      </ul>

      <h3>Termination:</h3>
      <p>We may terminate or suspend your account for violations of these Terms or misuse of the platform.</p>

      <h3>Notices:</h3>
      <p>Legal or other formal notices should be sent to: info@apointa.com or Apointa (FZC), Block C VL10-049, SRTIP, Sharjah, UAE.</p>

      <h3>Governing Law:</h3>
      <p>These Terms are governed by UAE law. You agree to submit to the non-exclusive jurisdiction of UAE courts.</p>
      <p>
        Any purchase, dispute or claim arising out of or in connection with this website shall be governed and construed in accordance with the laws
        of UAE.
      </p>

      <h3>Modifications:</h3>
      <p>
        We may update these Terms at any time. Material changes will be communicated via email or notices on the platform. Continued use of the
        Platform after changes implies acceptance.
      </p>

      <h3>Support:</h3>
      <p>Have a question or issue? Email us at info@apointa.com. We're here to help.</p>

      <h3>Contact Us:</h3>
      <p>
        Apointa (FZC)
        <br />
        info@apointa.com
        <br />
        M: +971 50 732 7832
        <br />
        Apointa (FZC), registered in Sharjah Research Technology and Innovation Park, Block C VL10-049, Sharjah, UAE
      </p>
    </>
  );
}

export default TermsContent;
